import { cn } from '@dreamstack/util/src/cn'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { GridWithGaps } from '../../layout/MainGrid'
import type { PropertySearchContextProviderOptions } from '../../next/PropertySearchContext'
import { PropertySearchContextProvider } from '../../next/PropertySearchContext'
import { PropertyFilter } from './PropertyFilter'
import { PropertyFilterOverviewSeoText } from './PropertyFilterOverviewRegionDescription'
import { PropertyFilterOverviewResultProperties } from './PropertyFilterOverviewResultProperties'
import { PropertyIndexFilterResultTitle } from './PropertyIndexFilterResultTitle'
import { TableSwitch } from './table/TableSwitch'

const PropertyFilterOverviewResultPropertiesTable = dynamic(
  () => import('./PropertyFilterOverviewResultPropertiesTable')
)

const PropertyIndexFilterArea = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'hidden md:block md:col-start-1 md:col-span-4 lg:col-start-1 lg:col-span-3',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const PropertyIndexResultArea = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'col-start-1 col-span-12 md:col-start-5 md:col-span-8 lg:col-start-4 lg:col-span-9',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const PropertyIndexResultAreaInnerGrid = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <GridWithGaps
    className={cn('grid-cols-12 md:grid-cols-8 lg:grid-cols-9', className)}
    {...rest}
  >
    {children}
  </GridWithGaps>
)

const PropertyIndexFilterPropertyResults = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'col-start-1 col-span-12 -mt-px-96 md:col-start-1 md:col-span-8 md:-mt-px-196 lg:col-start-2 lg:col-span-7',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const PropertyFilterOverview: FunctionComponent<
  React.PropsWithChildren<PropertySearchContextProviderOptions>
> = (options) => {
  const [showTableView, setShowTableView] = useState(false)

  return (
    <PropertySearchContextProvider {...options}>
      <GridWithGaps>
        <TableSwitch
          showTableView={showTableView}
          setShowTableView={setShowTableView}
        />
        <PropertyIndexFilterArea>
          <PropertyFilter />
        </PropertyIndexFilterArea>
        <PropertyIndexResultArea>
          {showTableView ? (
            <PropertyFilterOverviewResultPropertiesTable />
          ) : (
            <PropertyIndexResultAreaInnerGrid>
              <PropertyIndexFilterResultTitle />
              <PropertyIndexFilterPropertyResults>
                <PropertyFilterOverviewResultProperties />
              </PropertyIndexFilterPropertyResults>
            </PropertyIndexResultAreaInnerGrid>
          )}
          <PropertyFilterOverviewSeoText seoText={options.seoText} />
        </PropertyIndexResultArea>
      </GridWithGaps>
    </PropertySearchContextProvider>
  )
}
